import React, { useState, useEffect } from 'react';
import { X, FileText, Loader2 } from 'lucide-react';
import Swal from 'sweetalert2';
import useApi from '../../utils/useApi';

const IdEditModal = ({ isOpen, onClose, onSave, document: doc, client, currentIdType, idExpiry, showAssignmentOption }) => {
    const { updateId } = useApi();
    const [expirationDate, setExpirationDate] = useState('');
    const [assignTo, setAssignTo] = useState(currentIdType || 'id');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen && idExpiry) {
            // Convert m/d/Y or other formats to YYYY-MM-DD for the date input
            try {
                const parsed = new Date(idExpiry);
                if (!isNaN(parsed.getTime())) {
                    const yyyy = parsed.getFullYear();
                    const mm = String(parsed.getMonth() + 1).padStart(2, '0');
                    const dd = String(parsed.getDate()).padStart(2, '0');
                    setExpirationDate(`${yyyy}-${mm}-${dd}`);
                } else {
                    setExpirationDate('');
                }
            } catch {
                setExpirationDate('');
            }
        } else if (isOpen) {
            setExpirationDate('');
        }
    }, [isOpen, idExpiry]);

    useEffect(() => {
        if (isOpen) {
            setAssignTo(currentIdType || 'id');
        }
    }, [isOpen, currentIdType]);

    const handleSave = async () => {
        if (!expirationDate) {
            await Swal.fire({
                title: 'Missing Date',
                text: 'Please enter an expiration date.',
                icon: 'warning',
                confirmButtonColor: '#3085d6',
            });
            return;
        }

        setIsSaving(true);
        try {
            const response = await updateId({
                document_id: doc.document_id,
                uid: client.id,
                type: assignTo,
                identification_expiration_date: expirationDate,
            });

            if (response.success) {
                await Swal.fire({
                    title: 'Updated!',
                    text: 'ID document updated successfully.',
                    icon: 'success',
                    confirmButtonColor: '#198754',
                });
                onSave();
                onClose();
            }
        } catch (error) {
            await Swal.fire({
                title: 'Error',
                text: error.message || 'Failed to update ID document',
                icon: 'error',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (!isSaving) {
            onClose();
        }
    };

    if (!isOpen || !doc) return null;

    const renderPreview = () => {
        if (doc.url) {
            if (doc.type?.includes('pdf')) {
                return (
                    <div className="w-full h-40 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                        <FileText className="w-10 h-10 text-gray-400 dark:text-gray-500" />
                        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">PDF Document</span>
                    </div>
                );
            }
            return (
                <div className="relative w-full h-40 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                    <img
                        src={doc.url}
                        alt={doc.file_name}
                        className="w-full h-full object-contain"
                    />
                </div>
            );
        }
        return null;
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-auto flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Edit ID Document
                        </h2>
                        <button
                            onClick={handleClose}
                            disabled={isSaving}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                            <X size={24} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-5">
                        {/* Document Preview */}
                        {renderPreview()}

                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                            {doc.file_name}
                        </p>

                        {/* Expiration Date */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Expiration Date
                            </label>
                            <input
                                type="date"
                                value={expirationDate}
                                onChange={(e) => setExpirationDate(e.target.value)}
                                disabled={isSaving}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Assign To (only for married filing jointly) */}
                        {showAssignmentOption && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Assign To
                                </label>
                                <div className="flex gap-4">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            name="assignTo"
                                            value="id"
                                            checked={assignTo === 'id'}
                                            onChange={(e) => setAssignTo(e.target.value)}
                                            disabled={isSaving}
                                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                        />
                                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-200">Primary ID</span>
                                    </label>
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            name="assignTo"
                                            value="spouse_id"
                                            checked={assignTo === 'spouse_id'}
                                            onChange={(e) => setAssignTo(e.target.value)}
                                            disabled={isSaving}
                                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                        />
                                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-200">Spouse ID</span>
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isSaving}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={isSaving}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 flex items-center"
                        >
                            {isSaving ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                'Save'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default IdEditModal;
